import { useEffect, useState } from 'react'

export interface Async<T> {
  data: T | null
  error: Error | null
  loading: boolean
}

/**
 * Run `fn` whenever `deps` change, aborting the in-flight call first so a slow
 * response can never overwrite the result of a newer one.
 */
export function useAsync<T>(fn: (signal: AbortSignal) => Promise<T>, deps: unknown[]): Async<T> {
  const [state, setState] = useState<Async<T>>({ data: null, error: null, loading: true })

  useEffect(() => {
    const ac = new AbortController()
    setState((s) => ({ ...s, loading: true, error: null }))

    fn(ac.signal).then(
      (data) => {
        if (!ac.signal.aborted) setState({ data, error: null, loading: false })
      },
      (error: unknown) => {
        if (ac.signal.aborted || (error instanceof Error && error.name === 'AbortError')) return
        setState({
          data: null,
          error: error instanceof Error ? error : new Error(String(error)),
          loading: false,
        })
      },
    )

    return () => ac.abort()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)

  return state
}

/** Debounce a rapidly-changing value (search box → network call). */
export function useDebounced<T>(value: T, ms = 200): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), ms)
    return () => clearTimeout(t)
  }, [value, ms])
  return debounced
}
